import { Calendar, X } from "lucide-react";

import { lastNDays, useTimeRange } from "@/lib/use-time-range";

const PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

/**
 * Compact time-range control that lives in the workspace header. Every canvas
 * reads the same `from`/`to` via `useTimeRange`, so changing the window here
 * filters every visualization at once.
 */
export function TimeRangeBar() {
  const tr = useTimeRange();

  const matches = (days: number) => {
    if (!tr.from || !tr.to) return false;
    const r = lastNDays(days);
    return r.from === tr.from && r.to === tr.to;
  };

  return (
    <div className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-[11px]">
      <Calendar className="h-3 w-3 text-muted-foreground" />
      <span className="text-muted-foreground uppercase tracking-wider mr-1 text-[10px]">
        Window
      </span>
      <button
        onClick={() => tr.clear()}
        className={`rounded px-2 py-0.5 transition ${
          !tr.isActive
            ? "bg-primary/15 text-primary"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        All
      </button>
      {PRESETS.map((p) => {
        const active = matches(p.days);
        return (
          <button
            key={p.label}
            onClick={() => {
              const r = lastNDays(p.days);
              tr.set(r.from, r.to);
            }}
            className={`rounded px-2 py-0.5 transition ${
              active
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        );
      })}
      <span className="h-3 w-px bg-border mx-0.5" />
      <input
        type="date"
        value={tr.from ?? ""}
        onChange={(e) => tr.set(e.target.value || null, tr.to)}
        className="bg-transparent text-foreground/85 outline-none font-mono w-[105px]"
        title="From"
      />
      <span className="text-muted-foreground">→</span>
      <input
        type="date"
        value={tr.to ?? ""}
        onChange={(e) => tr.set(tr.from, e.target.value || null)}
        className="bg-transparent text-foreground/85 outline-none font-mono w-[105px]"
        title="To"
      />
      {tr.isActive && (
        <button
          onClick={() => tr.clear()}
          className="text-muted-foreground hover:text-foreground p-0.5"
          title="Clear window"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
